// 장비 개체·드롭 굴림·능력치·강화 판정(D2a·D2b). 순수·결정적: 같은 씨앗이면 같은 결과라 서버 권위로 옮겨도 같은 공식이다(PRD 4.3).
// 데이터는 content/items(등급·옵션·강화표·천장)·economy(강화 비용·분해·판매)·world(드롭 표)·loot. three·DOM import 금지.
// 설계 기준(디아블로·로스트아크식): 개체 = 정의 id + 굴린 등급·아이템 레벨·옵션 + 강화 단계 + 단계별 천장 카운터. 능력치는 저장하지 않고 계산한다.
import { AFFIXES, BUFF_CAP, EQUIPS, ENHANCE_PITY, ENHANCE_TABLE, GEAR_GROWTH, GRADES, LEGEND_UNIQUES, MP_COST_CUT_CAP, NAMED_UNIQUES, SETS, SOCKET_CAP, gearBaseInto, uniquesFor } from './content/items';
import { DIFFICULTY_ILVL_ADD, DISMANTLE_STONES, ENGRAVE_EFFECT_PER_STEP, ENHANCE_STAT_BONUS, SELL_GOLD, SOCKET_MAX_DRILLED, enhanceTryCost, regionGoldMult, stoneTierForIlvl, type StoneTier } from './content/economy';
import { DROP_TABLES, ENHANCE_MAX } from './content/world';
import type { AffixDef, AffixId, Difficulty, DropTableId, EquipDef, EquipSlot, Grade, HeroId, LegendUniqueDef, NamedUniqueDef, RegionNo, SetDef } from './content/types';
import { ELIXIRS, GEM_CAP, GEM_CODES_ALL, GEM_COLORS, gemColorOf, gemValue, isGemCode } from './content/consumables';
import { SHOP_EQUIPS } from './content/townShop';
import { levelSyncMult } from './rebirth';
import { LEGACY_EQUIPS } from './content/legacyGear';

// ── 결정적 난수 ──────────────────────────────────────
/** 문자열 → 32비트 씨앗(FNV-1a). 연산 id·개체 uid에서 강화·드롭 씨앗을 만든다 */
export function hashSeed(text: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) { h ^= text.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return h >>> 0;
}
/** mulberry32 한 개 상태. 시뮬(tools/sim/pacing.rng)·게임(core/rng)과 같은 수열 */
export interface SeedRng { s: number }
export function seedRng(seed: number): SeedRng { return { s: seed >>> 0 }; }
export function nextRand(r: SeedRng): number {
  let t = (r.s = (r.s + 0x6d2b79f5) >>> 0);
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
}
/** 0..1 난수 공급자(게임 Rng.next·SeedRng 둘 다 받는다) */
export type Rand = () => number;

// ── 장비 개체 ────────────────────────────────────────
export interface GearAffix { readonly id: AffixId; readonly v: number }
/** 가방·장착 장비 한 개. enh·pity만 강화로 바뀐다(나머지는 드롭 때 고정) */
export interface GearItem {
  /** 개체 고유 id(드롭 문맥에서 정해진다, 장착·강화 연산이 가리킨다) */
  readonly uid: string;
  /** EQUIPS 정의 id. 모르는 정의(다음 빌드 콘텐츠)도 버리지 않고 능력치 0으로 둔다 */
  readonly def: string;
  readonly grade: Grade;
  /** 아이템 레벨(정의 레벨 + 난이도 가산). 기본 능력치·강화 비용·강화석 등급이 이것을 따른다 */
  readonly ilvl: number;
  readonly affixes: readonly GearAffix[];
  /** 강화 단계 0..15 */
  enh: number;
  /** 단계별 천장 실패 카운터(인덱스 = 올라갈 단계 1..15, 길이 16). 하락해도 보존, 그 단계 성공 시 0 */
  pity: number[];
  /** 전설 고유 효과 id(content/items LEGEND_UNIQUES). 전설만. 없던 이전 저장은 읽을 때 uid로 결정적으로 정한다 */
  readonly uniq?: string;
  /**
   * 소켓(D2c, PRD 4.5): 칸마다 박힌 보석 코드(content/consumables gemCode, 빈 칸 −1). 길이 = 등급 소켓 수(SOCKET_CAP).
   * 없으면 빈 소켓(이 필드 전 저장·새 드롭). 보석 박기·빼기만 바꾼다(장부 socket·unsocket 연산)
   */
  sock?: number[];
  /**
   * 이름 있는 유니크 id(content/items NAMED_UNIQUES, BALANCE-1028 3절). 선택 필드 — 없으면 일반 개체(이전 저장 그대로).
   * 등급은 영웅이고 이름·고정 효과·거래 가능 여부는 정의를 따른다. 모르는 id(다음 빌드 콘텐츠)는 효과 없이 보존한다
   */
  readonly nu?: string;
  /**
   * C4 대장간 심화 선택 필드(계약 forge/contract.ts). 모두 없으면 예전 개체 그대로(저장 버전도 안 올린다).
   * rr: 재련 카운터(굴림 씨앗·단조 증가) · lk: 잠근 옵션 줄 비트마스크 · rp: 비교 대기 중인 재련 결과(affixes와 같은 길이)
   * eg: 전설 각인 단계 · dr: 전설 소켓 천공 완료. 장부 연산이 제자리에서 바꾼다(sock과 같다)
   */
  rr?: number;
  lk?: number;
  rp?: readonly GearAffix[];
  eg?: number;
  dr?: 1;
}

// 상점 장비 정의(S7 content/townShop)도 이름·부위·능력치를 찾을 수 있게 넣는다(드롭 후보 candidates는 EQUIPS만 본다)
// 옛 상점 장비 전환 정의(ECONOMY 14절)도 같은 표에 넣는다(드롭·상점 후보가 아니다)
const DEF_BY_ID = new Map<string, EquipDef>([...EQUIPS, ...SHOP_EQUIPS, ...LEGACY_EQUIPS].map((e) => [e.id, e]));
const AFFIX_BY_ID = new Map<AffixId, AffixDef>(AFFIXES.map((a) => [a.id, a]));
export function affixDef(id: AffixId): AffixDef | undefined { return AFFIX_BY_ID.get(id); }
export function equipDef(id: string): EquipDef | undefined { return DEF_BY_ID.get(id); }
export function itemSlot(item: GearItem): EquipSlot | null { return DEF_BY_ID.get(item.def)?.slot ?? null; }
/** 장비 정의의 영웅 전용(heroLock). 전용이 없거나 정의를 모르면 null(모든 영웅이 쓴다) */
export function itemHeroLock(item: GearItem | null | undefined): HeroId | null { return item ? DEF_BY_ID.get(item.def)?.heroLock ?? null : null; }
/**
 * 그 무기를 그 영웅이 전투에 쓰는가(무기 칸은 계정 공유 하나, c022). 전용 영웅이 같거나 전용이 없으면 true, 비었으면 true(더할 것이 없다).
 * 영웅 미지정 = 카엘. 라이라 활(heroLock lyra)은 라이라 능력치에 들어가고 카엘 대검은 빠진다(lyra-weapon-damage)
 */
export function weaponUsableBy(item: GearItem | null | undefined, hero: string | undefined): boolean {
  const lock = itemHeroLock(item);
  return lock === null || lock === (hero ?? 'kael');
}
export function gradeDef(g: Grade) { return GRADES.find((x) => x.id === g)!; }
/** 소켓 칸 수 = 등급 소켓 수 + 천공(전설 +1, 상한 SOCKET_MAX_DRILLED) */
export function itemSockets(item: GearItem): number { return Math.min(SOCKET_MAX_DRILLED, SOCKET_CAP[item.grade] + (item.dr ? 1 : 0)); }
export function freshPity(): number[] { return new Array<number>(ENHANCE_MAX + 1).fill(0); }

// ── 드롭 굴림 ────────────────────────────────────────
/** 드롭 문맥: 지역·난이도·드롭 표·주운 영웅(무기는 그 영웅 전용) */
export interface DropContext {
  readonly region: RegionNo;
  readonly diff: Difficulty;
  readonly table: DropTableId;
  readonly hero: HeroId;
  /** 드롭률 보너스(옵션·비약·펫 합, BUFF_CAP.dropRate 안으로 자른다) */
  readonly dropRate?: number;
}
export type DropSource = 'mob' | 'elite' | 'boss';
/** 처치 한 번의 드롭 결정(장비 수·강화석 수). 장비 개체는 rollGear로 따로 만든다 */
export interface KillDrops { equips: number; bossEquips: number; stones: number }

/**
 * 처치 1회 드롭 수. 일반 몹: equipChance(×드롭률), 엘리트: eliteGuaranteed 확정 + 같은 확률 1개, 보스: guaranteed 확정(보스 풀).
 * 강화석: stoneChance 확률로 난이도 배율(DIFFICULTY_LOOT.stones, 노말 1.6·하드 2·헬 4.8 — 소수는 다시 굴린다)만큼. 시뮬 perKillDrops와 같은 기대값.
 * out을 채워 돌려준다(호출자가 재사용, 할당 없음).
 */
export function rollKillDrops(rand: Rand, ctx: DropContext, source: DropSource, stoneMult: number, out: KillDrops): KillDrops {
  const t = DROP_TABLES[ctx.table];
  const rate = 1 + Math.min(BUFF_CAP.dropRate, Math.max(0, ctx.dropRate ?? 0));
  out.equips = 0; out.bossEquips = 0; out.stones = 0;
  if (source === 'boss') out.bossEquips = t.guaranteed;
  else {
    if (source === 'elite') out.equips += t.eliteGuaranteed;
    if (rand() < t.equipChance * rate) out.equips++;
  }
  // 같은 난수 하나로 강화석(stoneChance × 난이도 배율)과 분해 대체 강화석(salvageStone, 1개)을 가른다 — 기존 난수 소비 순서 그대로
  const sr = rand();
  if (sr < t.stoneChance) {
    const whole = Math.floor(stoneMult), frac = stoneMult - whole;
    out.stones = whole + (frac > 0 && rand() < frac ? 1 : 0);
  } else if (sr < t.stoneChance + t.salvageStone) out.stones = 1;
  return out;
}

/**
 * 등급 가중을 step 단계 위로 민다(스페셜 챔피언 확정 장비, STAGE-WORLD-PRD 4절 '등급 가중 한 단계 위').
 * 이 표에서 가중이 양수인 등급만 사다리로 삼아 값을 한 칸씩 위 등급으로 옮기고, 맨 위 등급은 자기 값에 아래 값을 더한다.
 * 표에 없던 등급(예: 일반 스테이지의 세트)은 새로 생기지 않는다. step 0이면 같은 객체를 돌려준다. 드롭 순간에만 부른다(할당 허용).
 */
export function stepGradeWeights(w: Readonly<Record<Grade, number>>, step: number): Readonly<Record<Grade, number>> {
  if (step <= 0) return w;
  const ladder = GRADES.filter((g) => w[g.id] > 0).map((g) => g.id);
  if (ladder.length < 2) return w;
  const out: Record<Grade, number> = { common: 0, rare: 0, epic: 0, set: 0, legend: 0 };
  const top = ladder.length - 1;
  for (let k = 0; k <= top; k++) {
    const to = Math.min(top, k + step);
    out[ladder[to]] += w[ladder[k]];
  }
  return out;
}

/** 굴림 등급 범위(첫 클리어 보상 등). 둘 다 포함 */
export interface GradeBounds { readonly min: Grade; readonly max: Grade }
function clampWeights(w: Readonly<Record<Grade, number>>, b: GradeBounds): Record<Grade, number> {
  const lo = GRADES.findIndex((g) => g.id === b.min), hi = GRADES.findIndex((g) => g.id === b.max);
  const out = { common: 0, rare: 0, epic: 0, set: 0, legend: 0 } as Record<Grade, number>;
  let sum = 0;
  GRADES.forEach((g, i) => { if (i >= lo && i <= hi) { out[g.id] = w[g.id]; sum += w[g.id]; } });
  if (sum <= 0) out[b.min] = 1;
  return out;
}

function pickWeighted(rand: Rand, w: Readonly<Record<Grade, number>>): Grade {
  let sum = 0;
  for (const g of GRADES) sum += w[g.id];
  let x = rand() * sum;
  for (const g of GRADES) { x -= w[g.id]; if (x < 0 && w[g.id] > 0) return g.id; }
  for (let i = GRADES.length - 1; i >= 0; i--) if (w[GRADES[i].id] > 0) return GRADES[i].id;
  return 'common';
}

/** 지역·영웅별 드롭 후보 정의(부팅 때 한 번 만든다). base: 일반 베이스 6부위(무기는 그 영웅), set: 그 지역 세트 4부위 */
const CANDIDATES = new Map<string, { base: EquipDef[]; set: EquipDef[] }>();
function candidates(region: RegionNo, hero: HeroId): { base: EquipDef[]; set: EquipDef[] } {
  const key = region + ':' + hero;
  let c = CANDIDATES.get(key);
  if (!c) {
    const regionSet = SETS.find((s) => s.region === region)?.id;
    c = {
      base: EQUIPS.filter((e) => e.region === region && !e.setId && (e.slot !== 'weapon' || e.heroLock === hero)),
      set: EQUIPS.filter((e) => e.setId === regionSet),
    };
    CANDIDATES.set(key, c);
  }
  return c;
}

/** 등급에 따른 옵션 값 위치(0..1): 전설은 위쪽 절반, 세트·영웅은 위쪽 3/4, 나머지는 전 구간(PRD 4.2 등급 배율과 같은 결) */
export const AFFIX_FLOOR: Readonly<Record<Grade, number>> = { common: 0, rare: 0, epic: 0.25, set: 0.25, legend: 0.5 };
/** 옵션 값 = 범위 위의 q 위치(소수 4자리). 드롭 굴림(rolledItem)과 재련(forge)이 같은 반올림을 쓴다 */
export function affixValueAt(a: AffixDef, q: number): number { return Math.round((a.range[0] + (a.range[1] - a.range[0]) * q) * 10000) / 10000; }

/**
 * 장비 한 개 굴림. pool: 'wave'(일반 웨이브 gradeWeights, 전설 0) | 'boss'(보스 확정 bossGradeWeights, 전설은 여기만).
 * 정의: 세트 등급은 그 지역 세트 부위, 나머지는 지역 일반 베이스(무기는 주운 영웅 전용). 아이템 레벨 = 정의 레벨 + 난이도 가산.
 * 옵션: 등급 줄 수(GRADES.affixes) 만큼 정의 옵션 풀에서 겹치지 않게, 값은 옵션 범위 안(소수 4자리).
 * gradeStep: 등급 가중을 그만큼 위로 민다(기본 0 = 기존과 같은 난수 소비·결과).
 */
export function rollGear(rand: Rand, ctx: DropContext, pool: 'wave' | 'boss', uid: string, gradeStep = 0, bounds?: GradeBounds): GearItem {
  const t = DROP_TABLES[ctx.table];
  const weights = stepGradeWeights(pool === 'boss' ? t.bossGradeWeights : t.gradeWeights, gradeStep);
  // bounds(첫 클리어 보상): 그 표의 등급 확률을 [min, max] 안으로 좁혀 다시 정규화한다. 범위 안 확률이 모두 0이면 min 등급
  let grade = pickWeighted(rand, bounds ? clampWeights(weights, bounds) : weights);
  const c = candidates(ctx.region, ctx.hero);
  // 세트 정의가 없는 지역(데이터 공백)은 영웅 등급으로 내린다(빈손 드롭 없음)
  if (grade === 'set' && c.set.length === 0) grade = 'epic';
  const list = grade === 'set' ? c.set : c.base;
  const def = list[Math.min(list.length - 1, Math.floor(rand() * list.length))];
  return rolledItem(rand, def, grade, def.level + DIFFICULTY_ILVL_ADD[ctx.diff], uid);
}

/**
 * 정의·등급·아이템 레벨이 정해진 개체의 옵션(·전설 고유 효과)을 굴린다. rollGear의 뒷부분과 같은 난수 소비 순서다
 * (레이드 세트 부위처럼 정의를 직접 고르는 보상도 같은 규칙으로 만든다, RAID-PRD 6절)
 */
export function rolledItem(rand: Rand, def: EquipDef, grade: Grade, ilvl: number, uid: string): GearItem {
  const gd = gradeDef(grade);
  const n = gd.affixes[0] + Math.floor(rand() * (gd.affixes[1] - gd.affixes[0] + 1));
  const poolIds = def.affixPool.slice();
  const affixes: GearAffix[] = [];
  for (let k = 0; k < n && poolIds.length > 0; k++) {
    const i = Math.min(poolIds.length - 1, Math.floor(rand() * poolIds.length));
    const a = AFFIX_BY_ID.get(poolIds[i])!;
    poolIds.splice(i, 1);
    const q = AFFIX_FLOOR[grade] + (1 - AFFIX_FLOOR[grade]) * rand();
    affixes.push({ id: a.id, v: affixValueAt(a, q) });
  }
  // 전설 고유 효과: 그 부위 후보 중 하나(옵션 굴림 뒤 난수 1회 — 전설일 때만 소비)
  if (grade === 'legend') {
    const cands = uniquesFor(def.slot);
    if (cands.length) return { uid, def: def.id, grade, ilvl, affixes, enh: 0, pity: freshPity(), uniq: cands[Math.min(cands.length - 1, Math.floor(rand() * cands.length))].id };
  }
  return { uid, def: def.id, grade, ilvl, affixes, enh: 0, pity: freshPity() };
}

/** 전설인데 고유 효과가 없는 개체(이 필드 전 저장)에 uid로 결정적으로 효과를 정한다. 전설이 아니거나 이미 있으면 그대로 */
export function ensureUnique(item: GearItem): GearItem {
  if (item.grade !== 'legend' || item.uniq) return item;
  const d = DEF_BY_ID.get(item.def);
  const cands = d ? uniquesFor(d.slot) : [];
  if (!cands.length) return item;
  return { ...item, uniq: cands[hashSeed('uniq|' + item.uid) % cands.length].id };
}
const UNIQUE_BY_ID = new Map<string, LegendUniqueDef>(LEGEND_UNIQUES.map((u) => [u.id, u]));
/** 개체의 고유 효과 정의(전설이 아니거나 모르는 id면 undefined) */
export function uniqueOf(item: GearItem): LegendUniqueDef | undefined {
  if (item.grade !== 'legend') return undefined;
  return UNIQUE_BY_ID.get(ensureUnique(item).uniq ?? '');
}

// ── 이름 있는 유니크(BALANCE-1028 3절) ─────────────────
const NAMED_BY_ID = new Map<string, NamedUniqueDef>(NAMED_UNIQUES.map((u) => [u.id, u]));
/** 개체의 이름 있는 유니크 정의(없거나 모르는 id면 undefined) */
export function namedOf(item: GearItem): NamedUniqueDef | undefined { return item.nu === undefined ? undefined : NAMED_BY_ID.get(item.nu); }
/**
 * 유니크 개체를 만든다: 그 지역 일반 베이스 중 유니크 부위 정의(없으면 아무 지역의 같은 부위) + 영웅 등급 옵션 굴림 + nu.
 * 무기는 영웅 전용이라 유니크 목록에 두지 않는다. 정의를 못 찾으면 null(데이터 공백 — 빈손)
 */
export function rollNamedItem(rand: Rand, u: NamedUniqueDef, ctx: DropContext, uid: string): GearItem | null {
  const def = EQUIPS.find((e) => e.region === u.source.region && !e.setId && e.slot === u.slot) ?? EQUIPS.find((e) => !e.setId && e.slot === u.slot);
  if (!def) return null;
  return { ...rolledItem(rand, def, 'epic', def.level + DIFFICULTY_ILVL_ADD[ctx.diff], uid), nu: u.id };
}
/**
 * 거래 정보(레인 D 경매장이 읽는다): 이름 있는 유니크만 거래 가능, 시세 기준값 = 정의 marketBase. 나머지 장비는 귀속(tradable false, 기준값 0).
 * 강화 단계는 기준값에 넣지 않는다(시세는 시장이 정한다)
 */
export function tradeInfo(item: GearItem): { tradable: boolean; marketBase: number } {
  // C4: 재련(rr>0)·각인·천공한 장비는 거래할 수 없다(귀속 — 전설은 원래 귀속이지만 정책을 여기서 고정한다. 투자한 장비가 거래소로 새지 않게,
  // 재련한 이름 있는 유니크를 맡기면 서버 등록부의 고정 부분과 달라 '부정 표식'(item-owned)으로 오인되던 문제도 함께 막는다 — PR #388 리뷰 P1-B). 우편 복사는 카운터를 보존한다
  if ((item.eg ?? 0) > 0 || item.dr || (item.rr ?? 0) > 0) return { tradable: false, marketBase: 0 };
  const u = namedOf(item);
  return u ? { tradable: u.tradable, marketBase: u.marketBase } : { tradable: false, marketBase: 0 };
}
/** 스킬 실제 MP 소모(유니크 MP 소모 감소 반영, 올림). 게임 시전·HUD 가능 표시가 같은 값을 쓴다 */
export function effectiveMpCost(cost: number, cut: number): number { return cut > 0 ? Math.ceil(cost * (1 - cut)) : cost; }

// ── 능력치 ──────────────────────────────────────────
/** 장착 장비 합계. 퍼센트 값은 소수(0.05 = 5%). 버프 합산 캡(PRD 4.2)은 sumGear가 자른다 */
export interface GearTotals {
  attack: number; maxHp: number; def: number; crit: number;
  /** 방어 % 옵션(고정 방어에 곱한다) */
  defPct: number;
  atkPct: number; hpPct: number; critDmg: number; atkSpd: number; moveSpd: number; skillDmg: number;
  cooldown: number; lifesteal: number; damageTaken: number; dropRate: number; gold: number; xp: number; mpRegen: number; awakenGauge: number;
  /** 세트 효과 전용 칸: 기본 공격 피해·보스 피해·경직(그로기) 피해, 버팀 횟수 */
  basicDmg: number; bossDmg: number; staggerDmg: number; lastStand: number;
  /** 전설 고유 효과 칸(LEGEND_UNIQUES fx) */
  lowHpDmg: number; eliteDmg: number; critRefund: number; dodgeShield: number; killHeal: number; killMp: number; killAwaken: number;
  /** 이름 있는 유니크 칸(NAMED_UNIQUES fx, BALANCE-1028): 초당 MP 고정 회복·스킬 MP 소모 감소(MP_COST_CUT_CAP)·평타 MP 획득 가산·최대 MP 고정 */
  mpFlat: number; mpCostCut: number; basicMp: number; maxMpFlat: number;
  /** 외형 단계(ECONOMY 14절): 장착 갑옷 → 카엘 몸 T1~T3, 장착 무기 → 칼날 빛. 0 = 그 부위가 비었다(T1로 보인다) */
  armorTier: number; weaponTier: number;
}
export function makeGearTotals(): GearTotals {
  return { attack: 0, maxHp: 0, def: 0, crit: 0, defPct: 0, atkPct: 0, hpPct: 0, critDmg: 0, atkSpd: 0, moveSpd: 0, skillDmg: 0, cooldown: 0, lifesteal: 0, damageTaken: 0, dropRate: 0, gold: 0, xp: 0, mpRegen: 0, awakenGauge: 0, basicDmg: 0, bossDmg: 0, staggerDmg: 0, lastStand: 0, lowHpDmg: 0, eliteDmg: 0, critRefund: 0, dodgeShield: 0, killHeal: 0, killMp: 0, killAwaken: 0, mpFlat: 0, mpCostCut: 0, basicMp: 0, maxMpFlat: 0, armorTier: 0, weaponTier: 0 };
}
type BaseOut = { attack: number; maxHp: number; def: number; crit: number };
const GRADE_MULT = new Map<Grade, number>(GRADES.map((g) => [g.id, g.mult]));
const BASE_SCRATCH: BaseOut = { attack: 0, maxHp: 0, def: 0, crit: 0 };
/** 등급 → 외형 단계(ECONOMY 14절): 일반·희귀 T1, 영웅·세트 T2, 전설 T3 */
const GRADE_LOOK: Readonly<Record<Grade, 1 | 2 | 3>> = { common: 1, rare: 1, epic: 2, set: 2, legend: 3 };
/** 개체의 외형 단계 = max(정의 단계, 등급 단계). 모르는 정의는 등급만 */
export function itemLookTier(item: GearItem): 1 | 2 | 3 {
  const t = DEF_BY_ID.get(item.def)?.tier ?? 1, g = GRADE_LOOK[item.grade] ?? 1;
  return (t > g ? t : g) as 1 | 2 | 3;
}
/** itemBaseStats를 out에 채운다(할당 없음) */
/** sync = 레벨 동기화 배율(환생 회차, shared/rebirth levelSyncMult). 1 = 그대로 */
function itemBaseInto(out: BaseOut, item: GearItem, sync = 1): BaseOut {
  const d = DEF_BY_ID.get(item.def);
  if (!d) { out.attack = 0; out.maxHp = 0; out.def = 0; out.crit = 0; return out; }
  let g = GRADE_MULT.get(item.grade) ?? 1;
  if (d.fixed) {
    // 옛 상점 장비(ECONOMY 14절): 옛 표 값 그대로. 아이템 레벨 곡선·등급 배율을 타지 않고 강화 배율만 받는다
    out.attack = d.base.attack ?? 0; out.maxHp = d.base.maxHp ?? 0; out.def = d.base.def ?? 0; out.crit = d.base.crit ?? 0;
    g = 1;
  } else gearBaseInto(out, d.slot, item.ilvl);
  const e = ENHANCE_STAT_BONUS[Math.max(0, Math.min(ENHANCE_MAX, item.enh))] * sync;
  out.attack = Math.round(out.attack * g * e);
  out.maxHp = Math.round(out.maxHp * g * e);
  out.def = Math.round(out.def * g * e);
  // 목걸이의 유일한 기본 능력치라 강화 배율도 받는다(받지 않으면 목걸이 강화가 아무것도 바꾸지 않는다, PR #154 Codex P1)
  out.crit = Math.round(out.crit * g * e * 10000) / 10000;
  return out;
}
/** 개체 기본 능력치 = gearBase(부위, 아이템 레벨) × 등급 배율 × 강화 배율(공격·HP·방어·치명) */
export function itemBaseStats(item: GearItem): { attack: number; maxHp: number; def: number; crit: number } {
  return itemBaseInto({ attack: 0, maxHp: 0, def: 0, crit: 0 }, item);
}
/**
 * 방어 → 받는 피해 감소(디아블로식 곡선): def / (def + K), K = DEF_CURVE.k × 방어 성장^(몹 레벨 − 1).
 * K가 장비 방어 성장(GEAR_GROWTH.def)과 같은 비율로 커서, 그 지역 장비(희귀 +5 한 벌)로 그 지역 몹에게 약 10~25%가 된다.
 * 받는 피해 감소 옵션·세트와 곱해 합치고 BUFF_CAP.damageTaken(50%)까지(combat.hurtHero).
 */
export const DEF_CURVE = { k: 60 } as const;
export function defReduction(def: number, mobLevel: number): number {
  if (!(def > 0)) return 0;
  const k = DEF_CURVE.k * Math.pow(GEAR_GROWTH.def, Math.max(0, mobLevel - 1));
  return def / (def + k);
}
/** 방어·받는 피해 감소 옵션을 합친 받는 피해 배율(1 − 감소, 감소는 BUFF_CAP 50%까지) */
export function gearDamageTakenMult(def: number, damageTakenMult: number, mobLevel: number): number {
  const keep = (1 - defReduction(def, mobLevel)) * damageTakenMult;
  return Math.max(1 - BUFF_CAP.damageTaken, keep);
}

// ── 합계 표(부팅 때 한 번): sumGear가 프레임·고정 스텝 안에서 객체를 만들지 않게 효과 칸·값을 배열로 펴 둔다(c015, AGENTS.md 프레임 루프 규칙) ──
const UNIQUE_INDEX = new Map<string, number>(LEGEND_UNIQUES.map((u, i) => [u.id, i]));
const UNIQUE_FX_KEYS: readonly (readonly string[])[] = LEGEND_UNIQUES.map((u) => Object.keys(u.fx));
const UNIQUE_FX_VALS: readonly (readonly number[])[] = LEGEND_UNIQUES.map((u) => Object.values(u.fx) as number[]);
const NAMED_INDEX = new Map<string, number>(NAMED_UNIQUES.map((u, i) => [u.id, i]));
const NAMED_FX_KEYS: readonly (readonly string[])[] = NAMED_UNIQUES.map((u) => Object.keys(u.fx));
const NAMED_FX_VALS: readonly (readonly number[])[] = NAMED_UNIQUES.map((u) => Object.values(u.fx) as number[]);
const NAMED_SEEN = new Uint8Array(NAMED_UNIQUES.length);
const SET_INDEX = new Map<string, number>(SETS.map((st, i) => [st.id, i]));
/** 정의 id → 세트 번호(세트가 아니면 없음) */
const DEF_SET = new Map<string, number>(EQUIPS.filter((e) => e.setId && SET_INDEX.has(e.setId)).map((e) => [e.id, SET_INDEX.get(e.setId!)!]));
const SET_FX2_KEYS = SETS.map((st) => Object.keys(st.fx2)), SET_FX2_VALS = SETS.map((st) => Object.values(st.fx2) as number[]);
const SET_FX4_KEYS = SETS.map((st) => Object.keys(st.fx4)), SET_FX4_VALS = SETS.map((st) => Object.values(st.fx4) as number[]);
const TOTAL_KEYS: readonly string[] = Object.keys(makeGearTotals());
/** sumGear 한 번 안에서 이미 더한 고유 효과·세트 부위 수(재사용, 끝나면 0) */
const UNIQUE_SEEN = new Uint8Array(LEGEND_UNIQUES.length);
const SET_COUNT = new Uint8Array(SETS.length);
/** sumGear 한 번 안의 보석 색별 합(재사용, D2c). 끝에 GEM_CAP으로 잘라 합계 칸에 더한다 */
const GEM_SUM = new Float64Array(GEM_COLORS.length);
const GEM_STAT: readonly string[] = GEM_COLORS.map((c) => c.stat);
const GEM_CAPS: readonly number[] = GEM_COLORS.map((c) => GEM_CAP[c.stat]);
/** 보석 코드 → 값 표(부팅 때 한 번) */
const GEM_VAL: readonly number[] = Array.from({ length: GEM_CODES_ALL }, (_, i) => gemValue(i));
const GEM_COLOR_OF: readonly number[] = Array.from({ length: GEM_CODES_ALL }, (_, i) => gemColorOf(i));
/** 개체의 고유 효과 번호(LEGEND_UNIQUES 순서, 없으면 −1). uniq가 있는 개체는 할당 없이 찾는다(효과 없는 이전 저장만 ensureUnique로 정한다) */
function uniqueIndexOf(item: GearItem): number {
  if (item.grade !== 'legend') return -1;
  const id = item.uniq ?? ensureUnique(item).uniq;
  return id === undefined ? -1 : UNIQUE_INDEX.get(id) ?? -1;
}
function addFx(o: Record<string, number>, keys: readonly string[], vals: readonly number[]): void {
  for (let i = 0; i < keys.length; i++) o[keys[i]] += vals[i];
}
/** 고유 효과를 mult배로 더한다(전설 각인, C4). mult가 1이면 addFx와 같은 값 */
function addFxMul(o: Record<string, number>, keys: readonly string[], vals: readonly number[], mult: number): void {
  for (let i = 0; i < keys.length; i++) o[keys[i]] += vals[i] * mult;
}
/** sumGear 한 번 안에서 고유 효과별 최대 각인 단계(재사용, 끝나면 0). 같은 효과가 두 부위에 있으면 높은 단계가 이긴다 */
const UNIQUE_EG = new Uint8Array(LEGEND_UNIQUES.length);

/**
 * 장착 장비 합계를 out에 채운다(할당 없음 — 효과 표는 부팅 때 펴 두고 세트 부위 수·고유 효과 중복은 고정 배열로 센다). 버프 합산 캡: 드롭률·골드·EXP·쿨감·피해감소
 */
/**
 * syncLevel(레인 R, REBIRTH-MODES-SPEC 2.2): 0 = 레벨 동기화 없음(1회차 그대로). 1 이상이면 부위마다 기본 능력치(공격·HP·방어·치명, 강화 포함)에
 * min(1, (syncLevel + 10) / 아이템 레벨)을 곱한다. 옵션 %·세트·전설·보석은 그대로(비율 효과라 레벨 동기화 대상이 아니다)
 */
export function sumGear(out: GearTotals, items: readonly (GearItem | null | undefined)[], syncLevel = 0): GearTotals {
  const o = out as unknown as Record<string, number>;
  for (let i = 0; i < TOTAL_KEYS.length; i++) o[TOTAL_KEYS[i]] = 0;
  // 전설 각인(C4): 같은 고유 효과가 여러 부위에 있어도 한 번만 더하므로, 그 효과의 가장 높은 각인 단계를 먼저 모은다(할당 없음)
  for (let n = 0; n < items.length; n++) {
    const it = items[n];
    if (!it || !it.eg || it.grade !== 'legend') continue;
    const ui = uniqueIndexOf(it);
    if (ui >= 0 && it.eg > UNIQUE_EG[ui]) UNIQUE_EG[ui] = Math.min(255, it.eg);
  }
  for (let n = 0; n < items.length; n++) {
    const it = items[n];
    if (!it) continue;
    const b = itemBaseInto(BASE_SCRATCH, it, syncLevel > 0 ? levelSyncMult(syncLevel, it.ilvl) : 1);
    out.attack += b.attack; out.maxHp += b.maxHp; out.def += b.def; out.crit += b.crit;
    const dslot = DEF_BY_ID.get(it.def)?.slot;
    if (dslot === 'armor') out.armorTier = Math.max(out.armorTier, itemLookTier(it));
    else if (dslot === 'weapon') out.weaponTier = Math.max(out.weaponTier, itemLookTier(it));
    // 옵션 id = 합계 키(방어 옵션은 퍼센트라 defPct). 치명 옵션은 기본 치명(목걸이)과 같은 칸에 더한다
    const af = it.affixes;
    for (let k = 0; k < af.length; k++) { const key = af[k].id === 'def' ? 'defPct' : af[k].id; o[key] = (o[key] ?? 0) + af[k].v; }
    // 전설 고유 효과(PR #154 Codex 2차 P1). 같은 고유 효과는 겹치지 않는다(두 부위에 같은 효과면 한 번만, c015 — 치명타 환급 두 배 등 캡 우회 차단)
    const ui = uniqueIndexOf(it);
    if (ui >= 0 && UNIQUE_SEEN[ui] !== 1) { UNIQUE_SEEN[ui] = 1; addFxMul(o, UNIQUE_FX_KEYS[ui], UNIQUE_FX_VALS[ui], 1 + ENGRAVE_EFFECT_PER_STEP * UNIQUE_EG[ui]); }
    // 이름 있는 유니크(BALANCE-1028): 같은 유니크는 한 번만
    const ni = it.nu === undefined ? -1 : NAMED_INDEX.get(it.nu) ?? -1;
    if (ni >= 0 && NAMED_SEEN[ni] !== 1) { NAMED_SEEN[ni] = 1; addFx(o, NAMED_FX_KEYS[ni], NAMED_FX_VALS[ni]); }
    const si = DEF_SET.get(it.def);
    if (si !== undefined && SET_COUNT[si] < 255) SET_COUNT[si]++;
    // 박힌 보석(D2c): 색별로 모아 두었다가 끝에 상한(GEM_CAP)으로 자른다. 등급 소켓 수를 넘는 칸은 세지 않는다(저장 조작 방어)
    const sk = it.sock;
    if (sk) {
      const cap = itemSockets(it);
      for (let k = 0; k < sk.length && k < cap; k++) { const c = sk[k]; if (c >= 0 && c < GEM_CODES_ALL) GEM_SUM[GEM_COLOR_OF[c]] += GEM_VAL[c]; }
    }
  }
  for (let c = 0; c < GEM_SUM.length; c++) { if (GEM_SUM[c] > 0) o[GEM_STAT[c]] += Math.min(GEM_CAPS[c], GEM_SUM[c]); GEM_SUM[c] = 0; }
  // 세트 효과: 장착한 같은 세트 부위 수가 2·4 이상이면 fx2·fx4를 더한다(PR #154 Codex P1). activeSets(장비 창)와 같은 판정
  for (let si = 0; si < SET_COUNT.length; si++) {
    const c = SET_COUNT[si];
    if (c >= 2) addFx(o, SET_FX2_KEYS[si], SET_FX2_VALS[si]);
    if (c >= 4) addFx(o, SET_FX4_KEYS[si], SET_FX4_VALS[si]);
  }
  UNIQUE_SEEN.fill(0);
  UNIQUE_EG.fill(0);
  NAMED_SEEN.fill(0);
  SET_COUNT.fill(0);
  return capBuffTotals(out);
}

/** 버프 합산 캡(PRD 4.2: 옵션·비약·펫 합)을 합계에 다시 건다(비약을 더한 뒤) */
export function capBuffTotals(out: GearTotals): GearTotals {
  out.dropRate = Math.min(BUFF_CAP.dropRate, out.dropRate);
  out.gold = Math.min(BUFF_CAP.gold, out.gold);
  out.xp = Math.min(BUFF_CAP.xp, out.xp);
  out.cooldown = Math.min(BUFF_CAP.cooldown, out.cooldown);
  out.damageTaken = Math.min(BUFF_CAP.damageTaken, out.damageTaken);
  out.mpCostCut = Math.min(MP_COST_CUT_CAP, out.mpCostCut);
  return out;
}
/**
 * 켜진 비약(비트 = content/consumables ELIXIRS 순서)을 합계에 더하고 캡을 다시 건다(D2c, 할당 없음).
 * 힘 공격 % · 철벽 받는 피해 감소 · 행운 드롭률 · 지혜 EXP. 공격 %는 캡이 없고(옵션·보석과 더함), 나머지는 BUFF_CAP 안
 */
export function applyElixirs(out: GearTotals, mask: number): GearTotals {
  if (mask === 0) return out;
  const o = out as unknown as Record<string, number>;
  for (let i = 0; i < ELIXIRS.length; i++) if (mask & (1 << i)) o[ELIXIRS[i].stat] += ELIXIRS[i].value;
  return capBuffTotals(out);
}
/** 소켓 칸 배열(없으면 등급 소켓 수만큼 빈 칸). 표시·연산용 사본 */
export function socketsOf(item: GearItem): number[] {
  const n = itemSockets(item);
  const out = new Array<number>(n).fill(-1);
  const sk = item.sock;
  if (sk) for (let k = 0; k < n && k < sk.length; k++) out[k] = isGemCode(sk[k]) ? sk[k] : -1;
  return out;
}
/** 소켓 서명(장착 합계를 다시 계산할지 판단하는 작은 정수, 할당 없음) */
export function socketSig(item: GearItem): number {
  const sk = item.sock;
  if (!sk) return 0;
  let h = 0;
  for (let k = 0; k < sk.length; k++) h = h * 17 + (sk[k] + 1);
  return h;
}

/**
 * 장착 합계를 다시 계산할지 판단하는 서명(할당 없음): 소켓 + 각인 단계 + 옵션 값. 재련 결과를 고르거나(rerollPick) 각인을 올려도
 * 강화 단계·소켓은 그대로라, socketSig만으로는 합계가 낡는다(C4). 같은 서명이면 합계도 같다
 */
export function itemSig(item: GearItem): number {
  const h = socketSig(item);
  let a = (item.eg ?? 0) + 1;
  const af = item.affixes;
  for (let k = 0; k < af.length; k++) a = (a * 31 + Math.round(af[k].v * 10000)) % 1000003;
  return h + a * 1073741824;
}

/** 장착 중인 세트별 부위 수와 2·4세트 활성 여부(장비 창 표시와 sumGear가 같은 판정을 쓴다) */
export interface ActiveSet { readonly def: SetDef; readonly count: number; readonly on2: boolean; readonly on4: boolean }
export function activeSets(items: readonly (GearItem | null | undefined)[]): ActiveSet[] {
  const counts = new Map<string, number>();
  for (const it of items) {
    const sid = it ? DEF_BY_ID.get(it.def)?.setId : undefined;
    if (sid) counts.set(sid, (counts.get(sid) ?? 0) + 1);
  }
  const out: ActiveSet[] = [];
  for (const [sid, count] of counts) {
    const def = SETS.find((s) => s.id === sid);
    if (def) out.push({ def, count, on2: count >= 2, on4: count >= 4 });
  }
  return out;
}

// ── 강화(PRD 4.3, 시뮬 c001·D0a 확정 규칙 그대로) ─────────────────
/** +to로 올리는 시도의 기본 확률·천장 배율·실효 확률. 천장: 그 단계 실패마다 +20%, 최대 3배(ENHANCE_PITY) */
export function enhanceChance(item: GearItem): { to: number; base: number; mult: number; chance: number; fails: number; down: number } {
  const to = Math.min(ENHANCE_MAX, item.enh + 1);
  const st = ENHANCE_TABLE[to - 1];
  const fails = item.pity[to] ?? 0;
  const mult = Math.min(ENHANCE_PITY.max, 1 + ENHANCE_PITY.step * fails);
  return { to, base: st.p, mult, chance: Math.min(1, st.p * mult), fails, down: st.down };
}
/** 천장 게이지(0..1): 이 단계 배율이 상한(3배)까지 간 비율. 화면 공개용 */
export function pityGauge(item: GearItem): number {
  const c = enhanceChance(item);
  return (c.mult - 1) / (ENHANCE_PITY.max - 1);
}
/** 하락 구간(+13~+15)인지: 실패 시 DOWN 확률로 −1, 보호 주문서로 막는다 */
export function isDownStep(item: GearItem): boolean { return item.enh < ENHANCE_MAX && ENHANCE_TABLE[item.enh].down > 0; }

export type EnhanceOutcome = 'success' | 'keep' | 'down' | 'protected';
export interface EnhanceRoll { outcome: EnhanceOutcome; from: number; to: number; chance: number; roll: number }
/**
 * 강화 한 번(순수 판정). 씨앗 = 연산 id + 개체 uid(같은 연산을 다시 적용해도 같은 결과). 시뮬 simEnhance와 같은 순서로 난수를 쓴다:
 * 성공 판정 1회, 실패면 하락 판정 1회(하락 구간만). 보호 주문서(useProtect)는 실제로 하락이 날 때만 소모된다(시뮬 protects 계수와 같다).
 * item을 바꾸지 않는다 — applyEnhanceRoll이 바꾼다.
 */
export function rollEnhance(item: GearItem, seed: number, useProtect: boolean): EnhanceRoll {
  const r = seedRng(seed);
  const c = enhanceChance(item);
  const roll = nextRand(r);
  if (roll < c.chance) return { outcome: 'success', from: item.enh, to: c.to, chance: c.chance, roll };
  if (c.down > 0 && nextRand(r) < c.down) {
    return useProtect ? { outcome: 'protected', from: item.enh, to: item.enh, chance: c.chance, roll } : { outcome: 'down', from: item.enh, to: Math.max(0, item.enh - 1), chance: c.chance, roll };
  }
  return { outcome: 'keep', from: item.enh, to: item.enh, chance: c.chance, roll };
}
/** 판정을 개체에 적용: 성공이면 단계 +1·그 단계 카운터 0, 실패면 그 단계 카운터 +1(하락해도 보존), 하락이면 단계 −1 */
export function applyEnhanceRoll(item: GearItem, r: EnhanceRoll): void {
  if (r.outcome === 'success') { item.pity[r.to] = 0; item.enh = r.to; return; }
  const to = Math.min(ENHANCE_MAX, r.from + 1);
  item.pity[to] = (item.pity[to] ?? 0) + 1;
  if (r.outcome === 'down') item.enh = r.to;
}
/** 다음 강화 1회 비용(강화석 등급·수, 골드). economy.enhanceTryCost 그대로 */
export function nextEnhanceCost(item: GearItem): { tier: StoneTier; stones: number; gold: number } {
  return enhanceTryCost(Math.min(ENHANCE_MAX, item.enh + 1), item.ilvl, item.grade);
}

// ── 정리(판매·분해) ─────────────────────────────────
/** 정리 결과: 일반·희귀는 판매 골드(지역 골드 배율), 영웅 이상은 분해 강화석(그 아이템 레벨 등급). PRD 4.4·D0a */
export function salvageValue(item: GearItem): { gold: number; tier: StoneTier; stones: number } {
  const d = DEF_BY_ID.get(item.def);
  const region = (d?.region ?? 1) as RegionNo;
  return { gold: Math.round(SELL_GOLD[item.grade] * regionGoldMult(region)), tier: stoneTierForIlvl(item.ilvl), stones: DISMANTLE_STONES[item.grade] };
}

/** 표시용 이름(정의 이름 + 강화 단계) */
export function itemName(item: GearItem): string {
  const d = DEF_BY_ID.get(item.def);
  const base = item.nu !== undefined && NAMED_BY_ID.has(item.nu) ? NAMED_BY_ID.get(item.nu)!.name : d ? d.name : '알 수 없는 장비';
  return item.enh > 0 ? `+${item.enh} ${base}` : base;
}
